import {
  BarChart3,
  Settings,
  Package,
  TrendingUp,
  AlertTriangle,
  Clock,
  Calendar,
  Shield,
} from 'lucide-react';
import type { DependencyPillarDetails, DriftReport } from '../../../types/analysis.types';
import {
  MetricCard,
  SectionHeader,
  DistributionBar,
  BooleanIndicator,
  RiskImplicationsPanel,
} from './shared';
import { safeNum, formatAge, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: DependencyPillarDetails;
  driftData?: DriftReport | null;
}

export const DependencyDetails: React.FC<Props> = ({
  details,
  metricBg,
  trackColor,
  theme,
  driftData,
  score,
}) => {
  const d = details as unknown as Record<string, unknown>;
  const driftRatio = safeNum(d.driftRatio ?? d.drift_ratio) ?? 0;
  const outdated = safeNum(d.outdatedDependencies ?? d.outdated_dependencies) ?? 0;
  const total = safeNum(d.totalDependencies ?? d.total_dependencies) ?? 0;
  const oldestYears = safeNum(d.oldestPinnedYears ?? d.oldest_pinned_years);
  const hasDependabot = Boolean(d.hasDependabot ?? d.has_dependabot);
  const hasRenovate = Boolean(d.hasRenovate ?? d.has_renovate);
  const shareOlder2Years =
    driftData?.metrics?.shareOlderThan2Years ??
    safeNum(d.shareOlderThan2Years ?? d.share_older_than_2_years) ??
    0;

  const age = formatAge(oldestYears);
  const driftPct = Math.round(driftRatio * 100);

  const ageDistribution = driftData?.ageDistribution ?? [];
  const staleDependencies = driftData?.staleDependencies ?? [];

  const ageDistributionSum = ageDistribution.reduce((sum, b) => sum + b.count, 0);
  const totalForDistribution =
    driftData?.metrics?.totalDependencies || total || ageDistributionSum || 1;

  const hasAgeData = ageDistribution.length > 0 && ageDistributionSum > 0;

  const barColors = ['#ef4444', '#f97316', '#eab308', '#10b981'];
  const bucketLabels: Record<string, string> = {
    '7+': '7+ years',
    '4-6': '4-6 years',
    '2-4': '2-4 years',
    '<2': '<2 years',
  };

  const getRiskMessage = () => {
    if (driftData?.interpretation?.riskImplications)
      return driftData.interpretation.riskImplications;
    if (driftRatio > 0.7)
      return 'High dependency drift increases upgrade complexity and expands security exposure windows.';
    if (driftRatio > 0.5)
      return 'Moderate drift detected. Plan for incremental updates to avoid compounding technical debt.';
    if (oldestYears && oldestYears > 3)
      return 'Some dependencies are significantly outdated and may have known vulnerabilities.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total"
          value={total}
          subtext="dependencies"
          icon={<Package className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Outdated"
          value={outdated}
          subtext={`${driftPct}% of total`}
          icon={
            <AlertTriangle
              className="w-4 h-4"
              style={{ color: driftPct > 50 ? '#f97316' : undefined }}
            />
          }
          valueColor={driftPct > 50 ? '#f97316' : undefined}
          metricBg={metricBg}
        />
        <MetricCard
          label=">2 Years"
          value={`${Math.round(shareOlder2Years * 100)}%`}
          subtext="of dependencies"
          icon={<Clock className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        {age.value !== 'N/A' && (
          <MetricCard
            label="Oldest"
            value={age.value}
            subtext={age.unit + ' old'}
            icon={<Calendar className="w-4 h-4 text-secondary" />}
            metricBg={metricBg}
          />
        )}
      </div>

      {hasAgeData && (
        <div>
          <SectionHeader icon={<TrendingUp className="w-4 h-4" />} title="Age Distribution" />
          <div className="space-y-2">
            {ageDistribution.map((ageGroup, idx) => (
              <DistributionBar
                key={ageGroup.bucket}
                label={bucketLabels[ageGroup.bucket] || ageGroup.bucket}
                value={ageGroup.count}
                total={totalForDistribution}
                trackColor={trackColor}
                barColor={barColors[idx % barColors.length]}
              />
            ))}
          </div>
        </div>
      )}

      {!hasAgeData && total > 0 && (
        <div>
          <SectionHeader
            icon={<BarChart3 className="w-4 h-4" />}
            title="Dependency Health Distribution"
          />
          <div className="space-y-2">
            <DistributionBar
              label="Up-to-date"
              value={total - outdated}
              total={total}
              trackColor={trackColor}
              barColor="#10b981"
            />
            <DistributionBar
              label="Outdated"
              value={outdated}
              total={total}
              trackColor={trackColor}
              barColor="#f97316"
            />
          </div>
        </div>
      )}

      {staleDependencies.length > 0 && (
        <div>
          <SectionHeader
            icon={<AlertTriangle className="w-4 h-4" />}
            title="Stale Dependencies (Needs Attention)"
            iconColor="#f97316"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {staleDependencies.slice(0, 6).map((dep) => {
              const depAge = formatAge(dep.ageYears);
              return (
                <div
                  key={dep.name}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: metricBg }}
                >
                  <div>
                    <span className="text-sm font-medium text-primary">{dep.name}</span>
                    <span className="text-xs text-secondary ml-2">
                      {dep.pinned} → {dep.latest}
                    </span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                    {depAge.value}
                    {depAge.unit ? ` ${depAge.unit}` : ''} old
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <SectionHeader icon={<Settings className="w-4 h-4" />} title="Dependency Automation" />
        <div className="flex flex-wrap gap-4 text-sm">
          <BooleanIndicator
            value={hasDependabot}
            trueLabel="Dependabot"
            falseLabel="No Dependabot"
          />
          <BooleanIndicator value={hasRenovate} trueLabel="Renovate" falseLabel="No Renovate" />
        </div>
      </div>

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
